import { CHAT_SHARE_SEGMENT } from "@/constants";
import { clearExplicitLogout } from "@/helpers/auth.helpers";

/** Query key on /logout that names where to go after signing out. */
export const REDIRECT_QUERY_PARAM = "redirect";

/** Query value: after logout, send the user to global apps auth. */
export const GLOBAL_AUTH_REDIRECT = "global-auth";

const LOCAL_AUTH_URL = "http://localhost:4200/auth";
const LOCAL_PRODUCTS_URL = "http://localhost:4200/products";
const IQ_PRODUCT_TYPE = "IQ";

/**
 * Whether the shared-cookie poller should send the browser to accounts login.
 * The `/auth` route completes sign-in from `?token=` / `?user=` (or redirects
 * itself when those are absent). Bouncing from App-level polling during that
 * callback drops the query string and causes a 4200 ↔ 3001 redirect loop.
 *
 * Nothing else may suppress the bounce: signing out of another Saras app only
 * clears `saras_global`, while iQ's own token cookie and Firebase session stay
 * on every open tab, so treating either as "still signed in" keeps iQ open.
 */
export const shouldBounceToAccountsForMissingSharedCookie = (options: {
  pathname: string;
  search: string;
  hasSharedCookie: boolean;
  isLocalDev: boolean;
}): boolean => {
  const { pathname, search, hasSharedCookie, isLocalDev } = options;
  if (isLocalDev || hasSharedCookie) return false;
  if (pathname === "/auth" || pathname === "/logout") return false;
  if (new URLSearchParams(search).has("token")) return false;
  return true;
};

/** Product launcher URL. Localhost always uses the local apps host. */
export function resolveAppProductsUrl(
  hostname: string = window.location.hostname,
  authUrl: string = import.meta.env.VITE_PRODUCT_SARAS as string,
): string {
  if (hostname === "localhost") return LOCAL_PRODUCTS_URL;
  const url = new URL(authUrl);
  url.pathname = url.pathname.replace(/\/auth\/?$/, "/products");
  url.search = "";
  url.hash = "";
  return url.href;
}

/** Opens the Saras apps launcher without signing the user out. */
export function redirectToGlobalAppsProducts(): void {
  window.location.href = resolveAppProductsUrl();
}

/**
 * Where accounts should return after a switch-account sign-in.
 * The current page is /logout, and sending the new user there would sign them out again.
 * Localhost returns to this app's /auth; deployed builds use the IQ auth URL.
 */
export function resolveSwitchAccountReturnUrl(
  hostname: string = window.location.hostname,
  origin: string = window.location.origin,
  productAuthUrl: string = import.meta.env.VITE_PRODUCT_IQ as string,
): string {
  if (hostname === "localhost") return `${origin}/auth`;
  return productAuthUrl;
}

/** Accounts /auth URL. A missing saras_global cookie is the switch-account signal that host already handles. */
export function buildGlobalAuthUrl(
  authUrl: string,
  redirectTo: string,
  productType: string = IQ_PRODUCT_TYPE,
): string {
  const url = new URL(authUrl);
  const params = new URLSearchParams();
  params.set("redirectTo", redirectTo);
  params.set("productType", productType);
  url.search = params.toString();
  return url.href;
}

export const redirectToLogin = (redirectTo?: string) => {
  clearExplicitLogout();
  const isLocal = window.location.hostname === "localhost";
  const iqUrl = redirectTo ?? `${location.origin}${location.pathname}`;
  const authUrl = isLocal ? LOCAL_AUTH_URL : import.meta.env.VITE_PRODUCT_SARAS;
  window.location.href = buildGlobalAuthUrl(authUrl, iqUrl);
};
export const hasSamePath = (path: string) => {
  return window.location.pathname === path;
};

export const getChatBasePath = () => "chat";

/**
 * Whether `pathname` is inside the chat section (`/chat` or `/chat/<id>`).
 *
 * Deliberately segment-exact: a plain `startsWith('/chat')` also matches
 * `/chats` (the chat LIST route), which is exactly the case this guard exists to
 * exclude.
 */
export const isChatSectionPath = (pathname: string) => {
  const base = `/${getChatBasePath()}`;
  if (pathname.startsWith(`${base}/${CHAT_SHARE_SEGMENT}/`)) return false;
  return pathname === base || pathname.startsWith(`${base}/`);
};

/** iQ setup wizard — full-bleed layout without app rail (D12 / D15). */
export const isSetupSectionPath = (pathname: string) =>
  pathname === "/setup" || pathname.startsWith("/setup/");

/**
 * Whether clicking a chat nav item should (re)select its thread.
 *
 * openui's `selectThread` unconditionally resets `messages: []` as part of
 * selecting, so re-selecting the already-active thread wipes the visible
 * messages — and because the URL doesn't change on a same-thread click, nothing
 * repopulates them, leaving a blank chat view. Skip the selection when the
 * target is already active; still select when switching threads (incl. from no
 * active thread).
 */
export const shouldSelectChatThread = (
  selectedThreadId: string | null | undefined,
  targetId: string,
) => (selectedThreadId ?? "") !== targetId;
